import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import s from './OpenStreetMap.css';

const COLOMBIA_CENTER = { lat: 4.5709, lng: -74.2973 };

function validCoordinate(value) {
  return Number.isFinite(Number(value));
}

function normalizeCenter(center) {
  if (center && validCoordinate(center.lat) && validCoordinate(center.lng)) {
    return { lat: Number(center.lat), lng: Number(center.lng) };
  }
  return COLOMBIA_CENTER;
}

class OpenStreetMap extends React.Component {
  static propTypes = {
    center: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
    zoom: PropTypes.number,
    minZoom: PropTypes.number,
    maxZoom: PropTypes.number,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    markers: PropTypes.array,
    circleRadius: PropTypes.number,
    draggableMarker: PropTypes.bool,
    showLocationPin: PropTypes.bool,
    locationPinTitle: PropTypes.string,
    fitMarkers: PropTypes.bool,
    onMarkerDragEnd: PropTypes.func,
    onMoveEnd: PropTypes.func,
    onInteractionStart: PropTypes.func,
    onMapClick: PropTypes.func,
    className: PropTypes.string,
  };

  static defaultProps = {
    center: COLOMBIA_CENTER,
    zoom: 12,
    minZoom: 3,
    maxZoom: 18,
    height: '100%',
    markers: [],
    circleRadius: 0,
    draggableMarker: false,
    showLocationPin: false,
    locationPinTitle: 'Ubicación exacta registrada',
    fitMarkers: false,
  };

  constructor(props) {
    super(props);
    this.mapNode = null;
    this.map = null;
    this.L = null;
    this.layers = [];
    this.setMapNode = node => { this.mapNode = node; };
  }

  componentDidMount() {
    this.L = require('leaflet');
    const center = normalizeCenter(this.props.center);
    this.map = this.L.map(this.mapNode, {
      center: [center.lat, center.lng],
      zoom: this.props.zoom,
      minZoom: this.props.minZoom,
      maxZoom: this.props.maxZoom,
      zoomControl: true,
      scrollWheelZoom: false,
    });

    this.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
    }).addTo(this.map);

    this.map.on('moveend zoomend', () => this.emitMoveEnd());
    this.map.on('dragstart zoomstart', () => {
      if (this.props.onInteractionStart) this.props.onInteractionStart();
    });
    this.map.on('click', event => {
      if (this.props.onMapClick) {
        this.props.onMapClick({ lat: event.latlng.lat, lng: event.latlng.lng });
      }
    });
    this.syncLayers();
    window.setTimeout(() => this.map && this.map.invalidateSize(), 80);
  }

  componentDidUpdate(prevProps) {
    const previousCenter = normalizeCenter(prevProps.center);
    const center = normalizeCenter(this.props.center);
    const centerChanged = previousCenter.lat !== center.lat || previousCenter.lng !== center.lng;
    const layersChanged = prevProps.markers !== this.props.markers ||
      prevProps.circleRadius !== this.props.circleRadius ||
      prevProps.draggableMarker !== this.props.draggableMarker || centerChanged;
    const pinChanged = prevProps.showLocationPin !== this.props.showLocationPin || prevProps.locationPinTitle !== this.props.locationPinTitle;

    if (centerChanged && this.map && !this.props.fitMarkers) {
      this.map.setView([center.lat, center.lng], this.map.getZoom(), { animate: true });
    }
    if (layersChanged || pinChanged) this.syncLayers();
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.off();
      this.map.remove();
      this.map = null;
    }
  }

  emitMoveEnd() {
    if (!this.map || !this.props.onMoveEnd) return;
    const center = this.map.getCenter();
    const bounds = this.map.getBounds();
    this.props.onMoveEnd({
      lat: center.lat,
      lng: center.lng,
      zoom: this.map.getZoom(),
      sw_lat: bounds.getSouthWest().lat,
      sw_lng: bounds.getSouthWest().lng,
      ne_lat: bounds.getNorthEast().lat,
      ne_lng: bounds.getNorthEast().lng,
    });
  }

  clearLayers() {
    this.layers.forEach(layer => layer.remove());
    this.layers = [];
  }

  syncLayers() {
    if (!this.map || !this.L) return;
    this.clearLayers();
    const center = normalizeCenter(this.props.center);
    const bounds = [];

    if (this.props.circleRadius > 0) {
      const circle = this.L.circle([center.lat, center.lng], {
        radius: this.props.circleRadius,
        color: '#176b4a',
        weight: 2,
        fillColor: '#69ad2f',
        fillOpacity: .22,
      }).addTo(this.map);
      this.layers.push(circle);
      bounds.push([center.lat, center.lng]);
    }

    if (this.props.draggableMarker || this.props.showLocationPin) {
      const icon = this.L.divIcon({
        className: s.locationPinWrapper,
        html: '<span class="' + s.locationPin + '"><i></i></span>',
        iconSize: [38, 48],
        iconAnchor: [19, 46],
      });
      const marker = this.L.marker([center.lat, center.lng], { icon, draggable: this.props.draggableMarker, autoPan: true, title: this.props.locationPinTitle }).addTo(this.map);
      marker.on('dragend', event => {
        const point = event.target.getLatLng();
        if (this.props.onMarkerDragEnd) this.props.onMarkerDragEnd({ lat: point.lat, lng: point.lng });
      });
      this.layers.push(marker);
      bounds.push([center.lat, center.lng]);
    }

    this.props.markers.forEach(item => {
      if (!validCoordinate(item.lat) || !validCoordinate(item.lng)) return;
      const point = [Number(item.lat), Number(item.lng)];
      const icon = this.L.divIcon({
        className: s.priceMarkerWrapper,
        html: '<span class="' + s.priceMarker + '">' + (item.label || '') + '</span>',
        iconSize: [92, 38],
        iconAnchor: [46, 34],
      });
      const marker = this.L.marker(point, { icon, title: item.title || 'Ubicación' }).addTo(this.map);
      if (item.popupHtml) marker.bindPopup(item.popupHtml, { className: s.listingPopup, maxWidth: 290, minWidth: 240 });
      if (item.onClick) marker.on('click', () => item.onClick(item));
      this.layers.push(marker);
      bounds.push(point);
    });

    if (this.props.fitMarkers && bounds.length > 0) {
      if (bounds.length === 1) this.map.setView(bounds[0], this.props.zoom);
      else this.map.fitBounds(bounds, { padding: [45, 45], maxZoom: 14 });
    }
  }

  render() {
    const style = { height: typeof this.props.height === 'number' ? `${this.props.height}px` : this.props.height };
    return <div className={cx(s.map, this.props.className)} style={style} ref={this.setMapNode} aria-label="Mapa interactivo de OpenStreetMap" />;
  }
}

export default withStyles(s)(OpenStreetMap);
